import {TcddTrainTab} from '../components/tab/tcdd-train-tab';
import {MainTab} from '../models/tcdd-train/main-tab.model';
import {SubTab} from '../models/tcdd-train/sub-tab.model';
import {InfoGroup} from '../models/tcdd-train/info-group.model';
import {TcddMainSystem} from '../enums/tcdd-main-system.enum';
import {TcddSubSystem} from '../enums/tcdd-sub-system.enum';
import {TcddSubSystemType} from '../enums/tcdd-sub-system-type.enum';

export type ValueIndex = { groupIndex: number; infoIndex: number } | null;

export class TcddTrainTabUtil {
  /**
   * Retrieves the info groups from a given sub tab based on the specified group type.
   *
   * @param mainTabId - ID of the main tab.
   * @param subTabId - ID of the sub tab.
   * @param subTabType - Specifies info groups as 'main' or 'sub'.
   * @returns The info groups array if found, otherwise null.
   */
  private static getInfoGroups(
    mainTabId: TcddMainSystem,
    subTabId: TcddSubSystem,
    subTabType: TcddSubSystemType
  ): InfoGroup[] | null {
    // Retrieve the main tab by ID
    const mainTab: MainTab = TcddTrainTab.getTabInfo()?.find(tab => tab.id === mainTabId);
    if (!mainTab) {
      console.warn(`Main tab with id "${mainTabId}" not found.`);
      return null;
    }

    // Retrieve the sub tab by ID
    const subTab: SubTab = mainTab.subTabs?.find(tab => tab.id === subTabId);
    if (!subTab) {
      console.warn(`Sub tab with id "${subTabId}" not found in main tab "${mainTabId}".`);
      return null;
    }

    // Retrieve the info groups
    const infoGroupKey = subTabType === TcddSubSystemType.MAIN ? 'mainInfoGroups' : 'subInfoGroups';
    const infoGroups: InfoGroup[] = subTab[infoGroupKey];
    if (!infoGroups) {
      console.warn(`Info groups ("${infoGroupKey}") not found in sub tab "${subTabId}".`);
      return null;
    }

    return infoGroups;
  }

  /**
   * Returns the indices of the item with the specified name.
   * @param mainTabId - The ID of the main tab.
   * @param subTabId - The ID of the sub tab.
   * @param subTabType - Specifies info groups as 'main' or 'sub'.
   * @param infoName - The name of the info to search for.
   * @returns An object containing the groupIndex and infoIndex, or null if not found.
   */
  public static getValueIndex(
    mainTabId: TcddMainSystem,
    subTabId: TcddSubSystem,
    subTabType: TcddSubSystemType,
    infoName: string
  ): ValueIndex {
    const infoGroups: InfoGroup[] | null = this.getInfoGroups(mainTabId, subTabId, subTabType);
    if (!infoGroups) return null;

    for (const [groupIndex, group] of infoGroups.entries()) {
      const {infoList} = group;
      if (!infoList) continue;

      const infoIndex = infoList.findIndex(info => info.name === infoName);
      if (infoIndex !== -1) {
        return {groupIndex, infoIndex};
      }
    }

    // Return null if the specified value is not found.
    return null;
  }
}
